export function formatCop(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Math.round(Number(value));
  return `COP ${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n)}`;
}

/** Public card/detail price — never renders $0; uses approved fallback labels for NEW quote-only rows. */
export function formatPublicVehiclePrice(listing = {}) {
  if (listing.priceDisplayLabel) return listing.priceDisplayLabel;
  const price = listing.currentPriceCop ?? listing.priceCop;
  if (price == null || Number.isNaN(Number(price))) {
    if (String(listing.conditionStatus || "").toUpperCase() === "NEW") {
      return "Precio no disponible";
    }
    return "—";
  }
  if (Number(price) === 0) return "Precio no disponible";
  return formatCop(price);
}

export function formatConditionLabel(conditionStatus) {
  const value = String(conditionStatus || "").toUpperCase();
  if (value === "NEW") return "New";
  if (value === "USED") return "Used";
  return null;
}

export function formatMileage(km) {
  if (km == null) return "—";
  return `${new Intl.NumberFormat("es-CO").format(km)} km`;
}

export function fairValueBadge(listing) {
  const label = listing.fairValueLabelDisplay || listing.fairValueLabel;
  const confidence = listing.fairValueConfidenceLabel || listing.confidenceLabel;
  if (
    !label ||
    label === "Not enough data" ||
    label === "Not Enough Data" ||
    listing.fairValueLabel === "INSUFFICIENT_DATA" ||
    confidence === "UNAVAILABLE" ||
    confidence === "LOW" ||
    confidence === "INSUFFICIENT_DATA"
  ) {
    return null;
  }
  const toneByLabel = {
    "Great price": "deal-great",
    "Great Deal": "deal-great",
    "Good Deal": "deal-good",
    "Fair price": "deal-fair",
    "Fair Price": "deal-fair",
    "Above market": "deal-high",
    "High Price": "deal-high",
    "High price": "deal-over",
    Overpriced: "deal-over",
  };
  let detail = null;
  if (listing.priceVsFairValueCop != null && listing.estimatedFairPriceCop) {
    const pct =
      listing.priceVsFairValuePct != null
        ? ` (${Math.abs(Number(listing.priceVsFairValuePct)).toFixed(1)}%)`
        : "";
    if (listing.priceVsFairValueCop < 0) {
      detail = `${formatCop(Math.abs(listing.priceVsFairValueCop))} below estimate${pct}`;
    } else if (listing.priceVsFairValueCop > 0) {
      detail = `${formatCop(listing.priceVsFairValueCop)} above estimate${pct}`;
    }
  }
  const explanation = listing.fairValueExplanation;
  return {
    label,
    detail: detail || (explanation ? String(explanation).slice(0, 80) : null),
    tone: toneByLabel[label] || "deal-fair",
    confidence,
  };
}

export function bestValueBadge(listing) {
  const label = listing.bestValueLabelDisplay || listing.bestValueLabel;
  if (!label || listing.bestValueLabel === "INSUFFICIENT_DATA") {
    return null;
  }
  const toneByLabel = {
    "Excellent Value": "bv-excellent",
    "Good Value": "bv-good",
    "Fair Value": "bv-fair",
    "Low Value": "bv-low",
    "Risky / Review": "bv-risky",
  };
  const score =
    listing.bestValueScore != null && listing.bestValueLabel !== "INSUFFICIENT_DATA"
      ? `Best Value: ${listing.bestValueScore}`
      : null;
  return {
    label,
    detail: score,
    tone: toneByLabel[label] || "bv-fair",
  };
}

export function depreciationBadge(listing) {
  if (
    !listing.depreciationLabel ||
    listing.depreciationLabel === "INSUFFICIENT_DATA" ||
    (listing.depreciationConfidenceScore != null &&
      listing.depreciationConfidenceScore < 60)
  ) {
    return null;
  }
  const label = listing.depreciationLabelDisplay || listing.depreciationLabel;
  if (listing.depreciationLabel === "TOO_CLOSE_TO_NEW") {
    return { label: "Too close to new", detail: null, tone: "dep-close-new" };
  }
  if (listing.depreciationLabel === "HOLDS_VALUE_WELL") {
    return { label: "Holds value well", detail: null, tone: "dep-holds" };
  }
  if (listing.depreciationPct != null && listing.newPriceCop) {
    const pct = Math.abs(listing.depreciationPct).toFixed(0);
    return {
      label: `${pct}% below new`,
      detail: label,
      tone: "dep-normal",
    };
  }
  return { label, detail: null, tone: "dep-normal" };
}

export function usedVsNewBadge(listing) {
  const confidence = listing.newPriceMatchConfidence;
  if (confidence == null || confidence < 70) return null;
  const pct = listing.usedVsNewDeltaPct;
  const deltaCop = listing.usedVsNewDeltaCop;
  if (pct == null) return null;

  if (pct > -5 && pct < 5) {
    return { label: "Close to new price", detail: null, tone: "near-new" };
  }
  if (pct <= -5) {
    const pctLabel = `${Math.abs(pct).toFixed(0)}% below new`;
    const copLabel =
      deltaCop != null ? `${formatCop(Math.abs(deltaCop))} below new` : null;
    return {
      label: pctLabel,
      detail: copLabel !== pctLabel ? copLabel : null,
      tone: "below-new",
    };
  }
  return {
    label: `${pct.toFixed(0)}% above new`,
    detail: deltaCop != null ? `${formatCop(deltaCop)} above new` : null,
    tone: "above-new",
  };
}

export function predictivePriceBadge(listing) {
  if (
    !listing.predictedPriceCop ||
    !listing.predictionConfidenceLabel ||
    !["HIGH", "MEDIUM"].includes(listing.predictionConfidenceLabel) ||
    (listing.predictionConfidenceScore != null && listing.predictionConfidenceScore < 65)
  ) {
    return null;
  }
  const detail =
    listing.priceVsPredictionPct != null
      ? `${Math.abs(Number(listing.priceVsPredictionPct)).toFixed(0)}% ${
          listing.priceVsPredictionPct < 0 ? "below" : "above"
        } model estimate`
      : null;
  return {
    label: `Model estimate: ${formatCop(listing.predictedPriceCop)}`,
    detail,
    tone: listing.priceVsPredictionPct < 0 ? "pred-below" : "pred-fair",
  };
}

export function priceChangeBadge(listing) {
  const type = listing.lastPriceChangeType;
  if (!type) return null;
  if (type === "PRICE_DECREASE" && listing.lastPriceDeltaCop != null) {
    const pct =
      listing.lastPriceDeltaPct != null
        ? ` (${Math.abs(Number(listing.lastPriceDeltaPct)).toFixed(1)}%)`
        : "";
    return {
      label: "Price dropped",
      detail: `Dropped ${formatCop(Math.abs(listing.lastPriceDeltaCop))}${pct}`,
      tone: "drop",
    };
  }
  if (type === "PRICE_INCREASE") {
    return { label: "Price increased", detail: null, tone: "up" };
  }
  if (type === "NEW_LISTING" || type === "INITIAL") {
    return { label: "New listing", detail: null, tone: "new" };
  }
  return null;
}
